import React, { useState } from "react";
import { Link } from "react-router-dom";
import "../styles/Login.css";
import { 
  TextField, 
  Button, 
  Container, 
  Paper, 
  Typography, 
  Box,
  Alert,
  CircularProgress,
  Link as MuiLink
} from '@mui/material';
import { 
  LockReset,
  CheckCircle,
  ArrowBack
} from '@mui/icons-material';
import { API_URL } from '../config';

const ForgotPassword = () => {
  const [identifier, setIdentifier] = useState("");
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setLoading(true);

    try {
      const response = await fetch(API_URL + "/account/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: identifier.trim() }),
      });

      const data = await response.json();

      if (response.ok) {
        setSent(true);
        setMaskedEmail(data.maskedEmail);
        setMessage({ type: "success", text: data.maskedEmail 
          ? `Un email a été envoyé à l'adresse ${data.maskedEmail}` 
          : "Si un compte est associé à cet identifiant, un email a été envoyé." 
        });
      } else {
        setMessage({ type: "error", text: data.error });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Erreur de connexion au serveur." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="login-page">
      <Container maxWidth="sm" sx={{ paddingTop: '120px', paddingBottom: '60px' }}>
        <Paper 
          elevation={8}
          className="login-card"
          sx={{
            padding: 4,
            background: 'linear-gradient(145deg, rgba(26, 10, 46, 0.9) 0%, rgba(20, 25, 45, 0.9) 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(156, 39, 176, 0.2)',
            borderRadius: '20px'
          }}
        >
          <Box sx={{ textAlign: 'center', marginBottom: 4 }}>
            <LockReset 
              sx={{ 
                fontSize: 60, 
                color: '#9C27B0',
                marginBottom: 2
              }} 
            />
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(135deg, #9C27B0 0%, #E1BEE7 50%, #FFD700 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: 1
              }}
            >
              Mot de passe oublié
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
            >
              Entrez votre pseudo ou email pour recevoir un lien de réinitialisation
            </Typography>
          </Box>

          {message && (
            <Alert 
              severity={message.type} 
              sx={{ 
                marginBottom: 3,
                borderRadius: 2,
                '& .MuiAlert-icon': {
                  color: message.type === 'success' ? '#9C27B0' : undefined
                }
              }}
              icon={message.type === 'success' ? <CheckCircle /> : undefined}
            >
              {message.text}
            </Alert>
          )}

          {!sent ? (
            <form onSubmit={handleSubmit}>
              <TextField 
                label="Pseudo ou Email"
                variant="outlined"
                fullWidth
                required
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                sx={{ marginBottom: 3 }}
                InputProps={{
                  sx: {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(156, 39, 176, 0.3)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(156, 39, 176, 0.5)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#9C27B0',
                    }
                  }
                }}
              />

              <Button 
                variant="contained" 
                type="submit" 
                fullWidth 
                size="large"
                disabled={loading}
                sx={{
                  background: 'linear-gradient(135deg, #9C27B0 0%, #673AB7 100%)',
                  color: '#ffffff',
                  fontWeight: 700,
                  padding: '14px',
                  fontSize: '1.05rem',
                  marginBottom: 3,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #7B1FA2 0%, #512DA8 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(156, 39, 176, 0.4)'
                  },
                  '&:disabled': {
                    background: 'rgba(156, 39, 176, 0.3)',
                    color: 'rgba(255, 255, 255, 0.5)'
                  }
                }}
              >
                {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : "Envoyer le lien"}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <MuiLink 
                  component={Link} 
                  to="/login" 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.6)',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: '0.9rem',
                    '&:hover': { color: '#9C27B0' }
                  }}
                >
                  <ArrowBack sx={{ fontSize: 18 }} />
                  Retour à la connexion
                </MuiLink>
              </Box>
            </form>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 3, lineHeight: 1.6 }}>
                Si un compte correspond, vous recevrez un email avec un lien valable <strong style={{ color: '#E1BEE7' }}>1 heure</strong>.
                <br />Pensez à vérifier vos spams.
              </Typography>
              <Button
                variant="outlined"
                component={Link}
                to="/login"
                startIcon={<ArrowBack />}
                sx={{ 
                  color: '#9C27B0', 
                  borderColor: 'rgba(156,39,176,0.4)',
                  '&:hover': {
                    borderColor: '#9C27B0',
                    background: 'rgba(156,39,176,0.1)'
                  }
                }}
              >
                Retour à la connexion
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default ForgotPassword;
